import { FileDetailsTabs, FileResponseInterface, FileUpload } from 'services/interface';
import { useCallback } from 'react';
import { navigate } from 'gatsby';
import { axios, AxiosConfig, MutationFetcher, SWRKey, useSWRConfig, useSWRMutation } from 'services/swr';
import { useToastNotify } from '@opswat/ui';
import { useApikeyLimits } from 'services/api/ApikeyLimits';

const getArchivePasswordHeaders = (passwords?: string[]): Record<string, string> => {
    if (!passwords || passwords.length === 0) {
        return {};
    }

    const filteredPasswords = passwords.map((password) => password.trim()).filter((password) => password !== '');

    if (filteredPasswords.length === 0) {
        return {};
    }

    if (filteredPasswords.length === 1) {
        return { archivepwd: filteredPasswords[0] };
    }

    return filteredPasswords.reduce<Record<string, string>>((headers, password, index) => {
        headers[`archivepwd${index + 1}`] = password;
        return headers;
    }, {});
};

const uploadFile: MutationFetcher<
    FileUpload,
    SWRKey,
    {
        file?: File;
        // When set, the backend downloads and scans the file from this URL (downloadfrom header) instead of a body.
        downloadUrl?: string;
        rules?: string;
        sandbox?: boolean;
        privateScan?: boolean;
        archivePasswords?: string[];
        sendArchivePasswords?: boolean;
        onProgress?: (progress: number) => void;
        signal?: AbortSignal;
    }
> = async (
    { url, ...config },
    {
        arg: { file, downloadUrl, rules, sandbox, privateScan, archivePasswords, sendArchivePasswords, onProgress, signal },
    },
) => {
    let data: FormData | undefined;
    if (file) {
        data = new FormData();
        data.append('file', file);
    }

    const hasUnarchiveRule = Boolean(rules?.split(',').some((rule) => rule.trim().toLowerCase() === 'unarchive'));

    const shouldSendArchivePasswords = Boolean(
        archivePasswords?.some((password) => password.trim() !== '') && (sendArchivePasswords || hasUnarchiveRule),
    );

    const headers = {
        ...config.headers,
        rule: rules,
        ...(file && { filename: encodeURIComponent(file.name) }),
        ...(downloadUrl && { downloadfrom: downloadUrl }),
        ...(sandbox && { sandbox: 'windows10' }),
        ...(privateScan && { privateprocessing: '1' }),
        ...(shouldSendArchivePasswords ? getArchivePasswordHeaders(archivePasswords) : {}),
    };

    const res = await axios.post(url, data, {
        ...config,
        headers,
        signal,
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
            }
        },
    });

    return res.data;
};

const hashLookupKey: SWRKey = { url: '/v4/hash/' };

const hashLookup: MutationFetcher<FileResponseInterface, SWRKey, string> = async (
    { url, ...config },
    { arg: hash },
) => {
    const hashUrl = url + hash;
    const res = await axios.get(hashUrl, config);
    return res.data;
};

const hashLookupSandboxKey: SWRKey = { url: '/v4/hash/' };

const hashLookupSandbox: MutationFetcher<FileResponseInterface, SWRKey, string> = async (
    { url, ...config },
    { arg: hash },
) => {
    const hashUrl = url + hash + '/sandbox';
    const res = await axios.get(hashUrl, config);
    return res.data;
};

export const useFileScan = () => {
    const fileUploadKey: SWRKey = { url: '/v4/file' };
    const {
        trigger: upload,
        isMutating: uploadIsMutating,
        ...hookData
    } = useSWRMutation(fileUploadKey, uploadFile, { revalidate: false });
    const { trigger: lookup, isMutating: lookupIsMutating } = useSWRMutation(hashLookupKey, hashLookup);
    const { trigger: lookupSandbox } = useSWRMutation(hashLookupSandboxKey, hashLookupSandbox);
    const { data: limitsStatus, mutate: refetchLimitsStatus } = useApikeyLimits();
    const isLoading = lookupIsMutating || uploadIsMutating;
    const { toastNotify } = useToastNotify();

    const { mutate: globalMutate } = useSWRConfig();

    // Shared submit + post-processing for both uploaded files and downloadfrom-URL scans.
    const runUpload = useCallback(
        async (arg: Parameters<typeof upload>[0], tab?: string) => {
            try {
                const fileData = await upload(arg);

                if (fileData?.data_id) {
                    const keys = new Set([
                        `/v4/hash/${fileData?.sha1}/sandbox`,
                        `/v4/hash/${fileData?.sha1}/vulnerability`,
                        `/v4/hash/${fileData?.sha1}/scanhistory`,
                        `/v4/comment/${fileData?.sha1}`,
                    ]);

                    await refetchLimitsStatus();

                    navigate(
                        `/results/file/${fileData.data_id}/${encodeURIComponent(tab || FileDetailsTabs.Overview)}`,
                        {
                            state: {
                                isSandboxOld: fileData?.sandbox_error ?? false,
                                dataId: fileData?.data_id,
                                limitsStatus,
                                sandboxId: fileData?.last_sandbox_id?.[0]?.sandbox_id || fileData?.sandbox_id,
                            },
                        },
                    );

                    globalMutate((key: AxiosConfig) => keys.has(key?.url ?? ''), undefined, { revalidate: true });
                }
            } catch (error) {
                const caughtError = error as {
                    code?: string;
                    name?: string;
                    message?: string;
                    messages?: string[];
                    response?: { data?: { error?: { message?: string; details?: { message?: string } } } };
                };
                if (caughtError?.code === 'ERR_CANCELED' || caughtError?.name === 'CanceledError') {
                    return;
                }
                let errorMessage = 'An error occurred while processing the file.';
                if (caughtError?.response?.data?.error?.details?.message) {
                    errorMessage = caughtError.response.data.error.details.message;
                } else if (caughtError?.response?.data?.error?.message) {
                    errorMessage = caughtError.response.data.error.message;
                } else if (caughtError?.messages?.length) {
                    errorMessage = caughtError.messages[0];
                } else if (caughtError?.message) {
                    errorMessage = caughtError.message;
                }
                toastNotify({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error',
                    hasClose: true,
                    duration: 5000,
                });
            }
        },
        [upload, refetchLimitsStatus, limitsStatus, globalMutate, toastNotify],
    );

    const handleUpload = useCallback(
        async (
            file: File,
            rules?: string,
            sandbox?: boolean,
            privateScan?: boolean,
            archivePasswords?: string[],
            onProgress?: (progress: number) => void,
            tab?: string,
            sendArchivePasswords?: boolean,
            signal?: AbortSignal,
        ) => {
            return runUpload(
                { file, rules, sandbox, privateScan, archivePasswords, sendArchivePasswords, onProgress, signal },
                tab,
            );
        },
        [runUpload],
    );

    // Scan a file the backend downloads from a URL (downloadfrom header) rather than an uploaded body.
    const handleScanUrl = useCallback(
        async (
            downloadUrl: string,
            rules?: string,
            sandbox?: boolean,
            privateScan?: boolean,
            tab?: string,
            signal?: AbortSignal,
        ) => {
            return runUpload({ downloadUrl, rules, sandbox, privateScan, signal }, tab);
        },
        [runUpload],
    );

    const handleScan = useCallback(
        async (
            file: File,
            rules?: string,
            sandbox?: boolean,
            privateScan?: boolean,
            archivePasswords?: string[],
            onProgress?: (progress: number) => void,
            tab?: string,
            sendArchivePasswords?: boolean,
            signal?: AbortSignal,
        ) => {
            return handleUpload(
                file,
                rules,
                sandbox,
                privateScan,
                archivePasswords,
                onProgress,
                tab,
                sendArchivePasswords,
                signal,
            );
        },
        [handleUpload],
    );

    return {
        scan: handleScan,
        scanUrl: handleScanUrl,
        rescan: handleUpload,
        lookup,
        lookupSandbox,
        isLoading,
        limitsStatus,
        ...hookData,
    };
};
